import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useActivities } from '../../api/queries/activities.ts'
import { useResultsHub } from '../../api/queries/assignments.ts'
import { useMe } from '../../api/queries/auth.ts'
import { useT } from '../../i18n/index.ts'
import { type PromptShown, track } from '../../lib/events.ts'
import { buttonClass } from '../../ui/Button.tsx'
import { IconCloudUp } from '../../ui/Icon.tsx'
import { keepWorkMessages } from './keepWorkMessages.ts'

/** Once per tab: a banner comes back on every visit, but it was shown to her once. */
export function noteShown(which: PromptShown, storageKey: string): void {
	try {
		if (sessionStorage.getItem(storageKey)) return
		sessionStorage.setItem(storageKey, '1')
	} catch {
		// Blocked storage: every showing counts, which only over-reports.
	}
	track({ kind: 'prompt_shown', data: { which } })
}

/**
 * Whether the layout's banner is up: a guest with any published set, on whatever page she is.
 * `undefined` while her sets are still loading. Whatever else asks a guest to keep her work
 * reads this, so two asks are never on screen at once.
 */
export function useKeepWorkBannerUp(): boolean | undefined {
	const { data: me } = useMe()
	const { data: activities } = useActivities({ enabled: !!me?.guest })
	if (!me?.guest) return false
	return activities?.some((a) => a.status === 'published')
}

/**
 * Whether anyone has handed in on any of her links, from the numbers the desk and Results already
 * read (`useResultsHub`: each learner once). One read while the banner is up, no polling. It is as
 * of the page load, and is read again when the desk or the results list opens after 30 s or she
 * changes a count: refetch on focus is off (app/queryClient.ts). "Anyone", not "a student": the
 * count cannot tell her own try on her link from a student's (nothing on an attempt names its
 * owner), so the headline says "Someone".
 */
function useSomeoneHandedIn(enabled: boolean): boolean {
	const { data: rows } = useResultsHub({ enabled })
	return !!rows?.some((r) => r.handedIn > 0)
}

/**
 * A guest who has published has students depending on work that lives in one browser. On
 * 2026-10-05 a guest ran a whole class (22 answers) and never signed up; the quiet header button
 * is not enough once that is at stake. The words are the share sheet's (keepWorkMessages): her
 * link keeps working, but only this browser can open her work and the answers. Once anyone has
 * handed in, the headline says that instead: still one note, one ask. A phone sees the headline
 * and the button only; the full sentence is for `sm` and up.
 */
export function KeepWorkBanner() {
	const t = useT(keepWorkMessages)
	const shown = useKeepWorkBannerUp() === true
	const handedIn = useSomeoneHandedIn(shown)
	useEffect(() => {
		if (shown) noteShown('keep_work', 'tt-prompt-keep-work')
	}, [shown])
	useEffect(() => {
		if (shown && handedIn) noteShown('keep_work_handed_in', 'tt-prompt-keep-work-handed-in')
	}, [shown, handedIn])
	if (!shown) return null

	return (
		// A note on the desk, not an alarm: white paper, one action.
		<div
			role="status"
			className="mb-6 flex items-center gap-3 rounded-card bg-sheet p-3 shadow-2 sm:gap-4 sm:p-4"
		>
			<span className="hidden size-10 shrink-0 place-items-center rounded-full bg-amber-200 text-ink sm:grid">
				<IconCloudUp className="size-5" />
			</span>
			<div className="min-w-0 flex-1">
				<p id="keep-work-title" className="text-body font-[560] text-ink">
					{handedIn ? t.handedIn : t.title}
				</p>
				{/* A phone gets the line and the button: the full note took half of a 360px screen. */}
				<p className="hidden text-small text-ink-2 sm:block">{t.body}</p>
			</div>
			<Link id="keep-work-save" to="/register" className={buttonClass('secondary', 'sm')}>
				{t.action}
			</Link>
		</div>
	)
}
